/**
 * Село: a Ukrainian village of the seventeenth century, one to a district.
 *
 * Built from a plan rather than raised as a single building, and that is the
 * important difference from the замок. There is one замок, cut once, and it
 * had to be *held* in memory for ever because «Пригода» throws away country
 * nobody is near and regrows it from the seed - a building written once does
 * not come back. Villages are everywhere: holding them all would be holding
 * the map. So a village is written the way trees are, column by column, out
 * of the seed and its own position. Forget it and walk back and it is the
 * same village, because it was never anywhere but in the arithmetic.
 *
 * That is why everything here takes world coordinates and a `put` that
 * quietly drops anything outside the column being grown. Each column writes
 * its own share of the same plan, and the shares add up to a village.
 *
 * The shape follows the plan Pavlo drew: ten to fifteen хат round a square
 * with a криниця in it, городи behind the houses, a млин out on the windward
 * side, a каплиця, господарські будівлі, and a тин round the lot.
 */
import {
  BlockId, FENCE, stoveId, doorId, bedId, chestId, gateId, FACING_STEP,
  beetCropId, type DoorFacing,
} from "../block/registry.js";
import type { VillagerKind } from "../entity/villager.js";

/** How much country a village has to itself. One per 500 by 500 blocks. */
export const VILLAGE_CELL = 500;

/** How far from a village's middle anything of it can stand. */
export const VILLAGE_REACH = 70;

/**
 * The most a village's site may rise and fall across, in blocks.
 *
 * "Може бути де завгодно крім води та гори, просто має адаптуватись під
 * поверхню." This is the "not a mountain" half of that, not a flatness rule:
 * the rule it replaced asked for seven blocks across the site and refused
 * four sites in five, and thirty is four storeys of hillside. What it is
 * really for is the case the biome misses - a cliff in the middle of a
 * meadow is a mountain whatever the map colour says.
 */
export const VILLAGE_SLOPE = 30;

/**
 * The most the ground under one building may stray from the height it is
 * given, before it is left out of the village altogether.
 *
 * Adapting to a slope is a хата cut a little into the bank at the back and
 * standing on a little made ground at the front. A хата on a thirteen-block
 * plinth is not adapting - it is levelling the hill and pretending - and
 * that is what came out of letting every building stand wherever the plan
 * put it. Four blocks either way is the most that still reads as a house on
 * a hillside; past that the village simply has a gap there, which is what a
 * real one has.
 */
export const VILLAGE_STEP = 4;

export type Put = (x: number, y: number, z: number, id: number) => void;

export type Kind =
  | "хата" | "велика хата" | "козацький" | "господарська"
  | "млин" | "каплиця" | "криниця" | "город"
  | "дороги" | "тин";

export interface Part {
  readonly kind: Kind;
  /** North-west corner, in world blocks. */
  readonly x: number;
  readonly z: number;
  readonly w: number;
  readonly d: number;
  /** Which way the door faces: 0 south, 1 west, 2 north, 3 east. */
  readonly facing: number;
}

export interface Village {
  readonly x: number;
  readonly z: number;
  readonly parts: readonly Part[];
}

/*
 * One hash, used for every decision a village makes.
 *
 * Everything here has to come out the same on the column that holds the
 * млин and on the column that holds the far corner of the тин, because
 * those are grown at different times by different calls and must agree.
 */
function hash(a: number, b: number, c: number): number {
  let h = (a * 374761393 + b * 668265263 + c * 2246822519) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = (h * 1274126177) >>> 0;
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** How many places in a district are tried before it is given up on. */
export const VILLAGE_TRIES = 36;

/**
 * Every place in this district a village might stand, in the order to try.
 *
 * This used to be one point - the middle of the district, jittered so the
 * map would not show a lattice - and whether there was a village at all was
 * whether *that one point* happened to be flat and dry. Over eight seeds
 * that gave a mean of two villages in eighty-one districts, and seeds with
 * none at all: "почав нову пригоду - там жодного села".
 *
 * Which was never a question about the country. Asking thirty-six places
 * instead of one, with exactly the same test applied to each, gives a mean
 * of thirty-six - because there is nearly always somewhere flat in five
 * hundred blocks, and looking at one point in the middle of it is not
 * looking.
 *
 * Six by six over the district's inner room, each jittered inside its own
 * share of it so the result is not a grid, and the order shuffled by the
 * seed so a village is not biased towards the north-west corner of its
 * district. Every candidate is still well inside its own five hundred, which
 * is what keeps two neighbours from ever growing through each other.
 */
export function villageTries(
  seed: number, gx: number, gz: number,
): { x: number; z: number }[] {
  const edge = VILLAGE_REACH + 40;
  const room = VILLAGE_CELL - 2 * edge;
  const side = Math.round(Math.sqrt(VILLAGE_TRIES));
  const cell = room / side;
  const out: { x: number; z: number; order: number }[] = [];
  for (let i = 0; i < side * side; i++) {
    const cx = i % side, cz = Math.floor(i / side);
    const jx = hash(gx * 131 + i, gz, seed + 17);
    const jz = hash(gx, gz * 131 + i, seed + 71);
    out.push({
      x: Math.round(gx * VILLAGE_CELL + edge + (cx + jx) * cell),
      z: Math.round(gz * VILLAGE_CELL + edge + (cz + jz) * cell),
      order: hash(gx * 7919 + i, gz * 104729, seed + 3),
    });
  }
  out.sort((a, b) => a.order - b.order);
  return out.map(({ x, z }) => ({ x, z }));
}

/**
 * The village laid out round its square, in world coordinates.
 *
 * Houses on rings rather than on a grid: a grid reads as a camp, and what
 * these settlements actually did was face the road and leave the garden
 * behind them. Each хата is put on a bearing from the middle with its door
 * turned inwards, so wherever you come in from you are looking at a front.
 */
export function planVillage(seed: number, at: { x: number; z: number }): Village {
  const parts: Part[] = [];

  /*
   * Nothing is built on top of anything else, and that took a village.
   *
   * "Один дім стоїть прямо на городі. Десь зникли ліжка." Both of those are
   * this: the houses went on two rings, the городи on a third and the barns
   * on the same radius as the городи, and none of them ever asked whether
   * the ground was already spoken for. Forty seeds gave four or five
   * overlapping pairs each - two городи on the same spot, a барн across a
   * bed - and because the городи are sown last, a garden laid over a хата
   * wrote soil across its floor and took the beds with it.
   *
   * So every part is claimed before it is added, and one that cannot have
   * its ground does not get built. A gap where a хата would have been is
   * what a real village on awkward ground has; a хата standing in somebody's
   * cabbages is not.
   */
  const taken: { x0: number; x1: number; z0: number; z1: number }[] = [];
  /** A block of air between neighbours, so eaves do not meet walls. */
  const APART = 1;
  const room = (x: number, z: number, w: number, d: number): boolean => {
    const b = { x0: x - APART, x1: x + w - 1 + APART,
      z0: z - APART, z1: z + d - 1 + APART };
    return !taken.some((t) =>
      b.x0 <= t.x1 && b.x1 >= t.x0 && b.z0 <= t.z1 && b.z1 >= t.z0);
  };
  const add = (kind: Kind, x: number, z: number, w: number, d: number,
    facing: number): boolean => {
    /*
     * The lanes and the тин are rings covering the whole village and are
     * never in anybody's way: they are drawn clipped, under everything.
     */
    const rings = kind === "дороги" || kind === "тин";
    if (!rings) {
      if (!room(x, z, w, d)) return false;
      taken.push({ x0: x, x1: x + w - 1, z0: z, z1: z + d - 1 });
    }
    parts.push({ kind, x, z, w, d, facing });
    return true;
  };

  // The криниця in the middle of the square, and the square left clear.
  add("криниця", at.x - 1, at.z - 1, 3, 3, 0);

  /*
   * Ten to fifteen dwellings, on two rings.
   *
   * The inner ring is the square itself and the outer one is the lane behind
   * it. Sizes come from the drawing: 5x7 for a хата, 7x9 for a big one,
   * 8x10 for the козацький будинок, of which a village has exactly one.
   */
  const howMany = 10 + Math.floor(hash(at.x, at.z, seed + 3) * 6);
  const cossack = Math.floor(hash(at.x, at.z, seed + 5) * howMany);
  for (let i = 0; i < howMany; i++) {
    const ring = i < 6 ? 0 : 1;
    const onRing = ring === 0 ? 6 : howMany - 6;
    const step = (Math.PI * 2) / onRing;
    const turn = (ring === 0 ? i : i - 6) * step +
      hash(i, ring, seed + 11) * step * 0.35 + (ring === 1 ? step / 2 : 0);
    const away = ring === 0 ? 20 : 34;
    const big = i !== cossack && hash(i, at.x, seed + 23) < 0.35;
    const w = i === cossack ? 8 : big ? 7 : 5;
    const d = i === cossack ? 10 : big ? 9 : 7;
    const kind: Kind = i === cossack ? "козацький" : big ? "велика хата" : "хата";
    const cx = at.x + Math.round(Math.cos(turn) * away);
    const cz = at.z + Math.round(Math.sin(turn) * away);
    // Facing the square: whichever wall is nearest the middle is the front.
    const facing = Math.abs(Math.cos(turn)) > Math.abs(Math.sin(turn))
      ? (Math.cos(turn) > 0 ? 1 : 3)
      : (Math.sin(turn) > 0 ? 2 : 0);
    if (!add(kind, cx - (w >> 1), cz - (d >> 1), w, d, facing)) continue;
    /*
     * And its garden, out beyond the houses rather than just behind it.
     *
     * Behind each house is where a garden belongs and where it will not fit:
     * the strip between the two rings is where the *other* ring's houses
     * stand, so every inner garden landed on an outer хата and the village
     * came out as one solid clump. Out past both rings there is room for all
     * of them, which is also where the ground actually was - the houses on
     * the lane, the plots behind the lot.
     *
     * Its ideal spot is straight out on the house's own bearing, and when
     * that is taken it steps outward and round until it finds ground -
     * twelve tries, and then the household works a neighbour's plot.
     */
    for (let t = 0; t < 12; t++) {
      const out = 46 + (t % 4) * 4;
      const swing = turn + (Math.floor(t / 4) - 1) * 0.16;
      const gx = at.x + Math.round(Math.cos(swing) * out) - 3;
      const gz = at.z + Math.round(Math.sin(swing) * out) - 3;
      if (add("город", gx, gz, 7, 7, 0)) break;
    }
  }

  // Господарські будівлі - stables and granaries - away on one side.
  const barns = 2 + Math.floor(hash(at.x, at.z, seed + 29) * 2);
  for (let i = 0; i < barns; i++) {
    const turn = Math.PI * 1.25 + i * 0.45 + hash(i, at.z, seed + 31) * 0.2;
    // They share a radius with the городи, so they need somewhere to go when
    // a garden has already taken it - out, the way the gardens themselves do.
    for (let t = 0; t < 6; t++) {
      if (add("господарська",
        at.x + Math.round(Math.cos(turn) * (46 + t * 5)) - 3,
        at.z + Math.round(Math.sin(turn) * (46 + t * 5)) - 5, 6, 10,
        Math.sin(turn) > 0 ? 2 : 0)) break;
    }
  }

  /*
   * The млин stands out on its own, which is not decoration.
   *
   * A windmill in among the roofs is a windmill in the lee of them. Every
   * village put it on the open side, and putting it there is the difference
   * between a model of a village and a village.
   */
  const millTurn = hash(at.x, at.z, seed + 37) * Math.PI * 2;
  for (let t = 0; t < 6; t++) {
    if (add("млин",
      at.x + Math.round(Math.cos(millTurn) * (52 + t * 4)) - 3,
      at.z + Math.round(Math.sin(millTurn) * (52 + t * 4)) - 4, 6, 8, 0)) break;
  }

  // And the каплиця, across the square from the млин.
  // Half a step off the outer ring's bearings, so it stands between two
  // хати rather than inside one.
  const chapelTurn = millTurn + Math.PI + Math.PI / 9;
  for (let t = 0; t < 8; t++) {
    // Round the ring rather than out from it: the каплиця belongs among the
    // houses, and a chapel pushed out past the городи is a chapel in a field.
    const swing = chapelTurn + (t % 2 === 0 ? 1 : -1) * Math.ceil(t / 2) * 0.18;
    if (add("каплиця",
      at.x + Math.round(Math.cos(swing) * 34) - 3,
      at.z + Math.round(Math.sin(swing) * 34) - 3, 6, 6,
      Math.sin(swing) > 0 ? 2 : 0)) break;
  }

  /*
   * The lanes, and the тин round the lot.
   *
   * Both are rings rather than boxes, so they are one part each covering the
   * whole village and drawn clipped - a column writes the arc that crosses
   * it. Cheaper than it sounds: a ring is a few hundred cells, and a column
   * that the ring misses does nothing at all.
   *
   * The тин has four ways through it, on the compass points, because a
   * village you have to climb into is a fort.
   */
  add("дороги", at.x - 50, at.z - 50, 101, 101, 0);
  add("тин", at.x - 58, at.z - 58, 117, 117, 0);

  return { x: at.x, z: at.z, parts };
}

/* ---- the buildings themselves ------------------------------------------ */

const fill = (
  put: Put, x0: number, x1: number, y0: number, y1: number,
  z0: number, z1: number, id: number,
): void => {
  for (let y = y0; y <= y1; y++) {
    for (let z = z0; z <= z1; z++) {
      for (let x = x0; x <= x1; x++) put(x, y, z, id);
    }
  }
};

/**
 * A хата: white walls, a straw roof, and a пiч inside.
 *
 * The roof is the part that has to be right. A flat lid reads as a shed, so
 * it is gabled - courses of straw stepping in from both long sides until
 * they meet - and it oversails the walls by a block all round, which is what
 * makes the deep shadow under the eaves that these houses are known for.
 */
function cottage(
  put: Put, p: Part, floor: number, tall: number, logs: number,
): void {
  const x1 = p.x + p.w - 1;
  const z1 = p.z + p.d - 1;
  // A footing of clay, so the white does not sit straight on the grass.
  fill(put, p.x, x1, floor, floor, p.z, z1, BlockId.Clay);
  // Walls, with log corners.
  fill(put, p.x, x1, floor + 1, floor + tall, p.z, z1, BlockId.Whitewash);
  for (const [cx, cz] of [[p.x, p.z], [x1, p.z], [p.x, z1], [x1, z1]]) {
    fill(put, cx!, cx!, floor + 1, floor + tall, cz!, cz!, logs);
  }
  // The room inside.
  fill(put, p.x + 1, x1 - 1, floor + 1, floor + tall, p.z + 1, z1 - 1,
    BlockId.Air);
  fill(put, p.x + 1, x1 - 1, floor, floor, p.z + 1, z1 - 1, BlockId.OakPlanks);

  /*
   * The door, in the middle of whichever wall faces the square.
   *
   * A real дубові двері rather than a hole. `Part.facing` is which wall the
   * door is in and `DoorFacing` is which way the panel looks, and they are
   * not numbered the same way - the mapping is written out rather than
   * calculated, because a door hung a quarter turn out is a door you walk
   * into.
   */
  const midX = (p.x + x1) >> 1;
  const midZ = (p.z + z1) >> 1;
  const door: [number, number] = [doorCell(p).x, doorCell(p).z];
  const looks = ([0, 3, 2, 1][p.facing] ?? 0) as DoorFacing;
  put(door[0], floor + 1, door[1], doorId(0, looks, false, false));
  put(door[0], floor + 2, door[1], doorId(0, looks, true, false));
  // Windows either side of it, one course under the eaves - glass, so the
  // house is lit inside and is not a box with two holes in the wall.
  const win = floor + tall - 1;
  if (p.facing === 0 || p.facing === 2) {
    const z = p.facing === 0 ? p.z : z1;
    put(midX - 2, win, z, BlockId.Glass);
    put(midX + 2, win, z, BlockId.Glass);
  } else {
    const x = p.facing === 1 ? p.x : x1;
    put(x, win, midZ - 2, BlockId.Glass);
    put(x, win, midZ + 2, BlockId.Glass);
  }

  // The піч, in the corner by the door, and its chimney.
  const stoveX = p.x + 1;
  const stoveZ = p.z + 1;
  fill(put, stoveX, stoveX, floor + 1, floor + 2, stoveZ, stoveZ,
    stoveId(0, 0));

  /*
   * What is inside: beds and a скриня.
   *
   * Two beds in a велика хата and in the козацький будинок, one in a small
   * хата - which is also what decides who lives there, so the count is
   * `bedsIn` and both this and the villagers read it rather than each
   * deciding for themselves.
   *
   * The beds lie along the far wall with their heads to it, out of the way
   * of the door and of the піч in the near corner; the скриня stands in the
   * corner opposite the піч. The room is three by five at its smallest, so
   * there is not much choice about any of it, and that is fine - these are
   * one-room houses.
   */


  /*
   * The straw roof, stepped in from the long sides.
   *
   * It starts one block proud of the walls on every side - the oversail -
   * and each course comes in one and rises one until the two sides meet. A
   * шіст-block house therefore gets three courses and a ridge, which is
   * about the pitch these roofs had.
   */
  const eave = floor + tall + 1;
  const half = Math.min(p.w, p.d) >> 1;
  for (let c = 0; c <= half; c++) {
    const y = eave + c;
    fill(put, p.x - 1 + c, x1 + 1 - c, y, y, p.z - 1 + c, z1 + 1 - c,
      BlockId.Thatch);
    // Hollow it as it goes, or the house is a solid block of straw.
    if (c < half) {
      fill(put, p.x + c, x1 - c, y, y, p.z + c, z1 - c, BlockId.Air);
    }
  }
  // The chimney through the straw.
  fill(put, stoveX, stoveX, floor + tall + 1, eave + half + 1, stoveZ, stoveZ,
    BlockId.Clay);
}

/**
 * The furniture, put in after every building in the village is up.
 *
 * A second pass and not part of `cottage`, which is where it started. The
 * plan puts buildings close enough that their padded boxes overlap, and
 * levelling a box writes air above the floor and earth below it - so a хата
 * built early had its beds quietly eaten by the ground being cut for its
 * neighbour. One dwelling in ten came out with no beds in it, and the walls
 * and the door were untouched, which is why it took a check that counted
 * beds *per house* to see it at all.
 *
 * Nothing here writes anything a building stands on, so it is safe to run
 * last.
 */
export function furnish(put: Put, p: Part, floor: number): void {
  if (bedsIn(p.kind) === 0) return;
  const lie = floor + 1;
  for (const [bx, bz] of bedsFor(p)) {
    // Head against the far wall, foot one step back into the room.
    put(bx, lie, bz, bedId(false, 2));
    const head = FACING_STEP[2]!;
    put(bx + head[0], lie, bz + head[1], bedId(true, 2));
  }
  const chest = chestCell(p);
  put(chest.x, lie, chest.z, chestId(0));
}

/**
 * How many beds a dwelling has, which is also how many people live in it.
 *
 * One number, asked by the builder and by whatever puts people in the
 * village, so a хата can never end up with two beds and one occupant or the
 * other way about.
 */
export function bedsIn(kind: Kind): number {
  return kind === "хата" ? 1 : kind === "велика хата" || kind === "козацький"
    ? 2 : 0;
}

/**
 * Where the beds lie in a dwelling, as the cell each foot stands in.
 *
 * Along the wall furthest from the піч, which is in the near corner, with
 * their heads to the wall. Two of them are side by side with a cell between,
 * because a хата is five wide inside at the most.
 */
export function bedsFor(p: Part): [number, number][] {
  const how = bedsIn(p.kind);
  if (how === 0) return [];
  const x1 = p.x + p.w - 1, z1 = p.z + p.d - 1;
  // The foot is one cell in from the far wall; the head goes against it.
  const bz = z1 - 2;
  const out: [number, number][] = [];
  if (how === 1) out.push([p.x + 1, bz]);
  else { out.push([p.x + 1, bz]); out.push([x1 - 1, bz]); }
  return out;
}

/**
 * Who lives in a dwelling, and where each of them sleeps.
 *
 * "Засели село селянами: там де 2 ліжка - жінку й чоловіка, а де одне ліжко -
 * рандомно чи жінку чи чоловіка. Створи дітей і дівчат і хлопців."
 *
 * Read off `bedsFor`, which is the same list the builder puts the beds down
 * from - so a хата can never end up with two beds and one occupant or the
 * other way about. Children have no bed of their own and sleep on the floor
 * of the room, which is both what the хата has room for and what these
 * houses actually were.
 *
 * Pure and deterministic: the same village puts the same people in the same
 * houses every time it is walked into, which is the rule everything in
 * «Пригода» is built on.
 */
export interface Dweller {
  readonly kind: VillagerKind;
  /** The cell they lie in at night. */
  readonly bed: { x: number; y: number; z: number };
  /** Where they are standing when the village is first walked into. */
  readonly at: { x: number; y: number; z: number };
}

export function household(p: Part, floor: number): Dweller[] {
  const beds = bedsFor(p);
  if (beds.length === 0) return [];
  const lie = floor + 1;
  const out: Dweller[] = [];
  const roll = (n: number): number => hash(p.x, p.z, 4400 + n);
  /*
   * Everybody starts in the middle of the room, and not in their own bed.
   *
   * A ліжко is a solid block. A body put down inside one is a body inside a
   * wall, and what the collision does with that is push it somewhere - out
   * through the хата's back wall, in the two tries this took. They walk to
   * bed at dusk like anybody else; standing beside it is near enough to be
   * in it.
   */
  const stand = {
    x: p.x + (p.w >> 1) + 0.5,
    y: lie,
    z: p.z + ((p.d - 2) >> 1) + 0.5,
  };

  if (beds.length === 1) {
    const [bx, bz] = beds[0]!;
    out.push({
      kind: roll(1) < 0.5 ? "чоловік" : "жінка",
      bed: { x: bx, y: lie, z: bz },
      at: stand,
    });
    return out;
  }

  const [manX, manZ] = beds[0]!;
  const [herX, herZ] = beds[1]!;
  out.push({
    kind: "чоловік",
    bed: { x: manX, y: lie, z: manZ },
    at: stand,
  });
  out.push({
    kind: "жінка",
    bed: { x: herX, y: lie, z: herZ },
    at: stand,
  });

  /*
   * And the children, in a house that has two grown-ups in it.
   *
   * None, one or two - a village where every house holds the same family is
   * a village of copies. They sleep on the floor in the middle of the room,
   * which is where there is space for them: both beds are along the far wall
   * and the піч and the скриня have the corners.
   */
  const many = roll(2) < 0.35 ? 0 : roll(3) < 0.6 ? 1 : 2;
  const midX = p.x + (p.w >> 1);
  const midZ = p.z + (p.d >> 1);
  for (let i = 0; i < many; i++) {
    out.push({
      kind: roll(10 + i) < 0.5 ? "хлопець" : "дівчина",
      // No bed of their own: they lie on the floor of the room, which is
      // both what the хата has space for and what these houses had.
      bed: { x: midX, y: lie, z: midZ - 1 - i },
      at: stand,
    });
  }
  return out;
}

/**
 * Each dwelling with the город that belongs to it.
 *
 * The plan lays a house down and then its garden, so the pairing is the
 * order they were added in. Written out here rather than left for whoever
 * needs it: "whichever город is nearest" is a different answer on a slope,
 * where the nearest one can belong to the neighbour.
 */
export function dwellings(
  village: Village,
): { house: Part; garden: Part | null }[] {
  const out: { house: Part; garden: Part | null }[] = [];
  village.parts.forEach((part, i) => {
    if (bedsIn(part.kind) === 0) return;
    const next = village.parts[i + 1];
    out.push({
      house: part,
      garden: next !== undefined && next.kind === "город" ? next : null,
    });
  });
  return out;
}

/**
 * Where in a город somebody stands to work it.
 *
 * A cell of the bed rather than the middle of the plot: the middle of a
 * seven-wide garden is the water channel, and a villager standing in the
 * channel is a villager standing in a ditch. One row in from the way in, on
 * the wheat side.
 */
export function plotCell(garden: Part, floor: number): {
  x: number; y: number; z: number;
} {
  return {
    x: garden.x + (garden.w >> 1),
    y: floor + 1,
    z: garden.z + 1,
  };
}

/**
 * The cell the door stands in.
 *
 * One answer, asked by the builder that hangs the door and by the people who
 * have to walk through it. "Вони мають розуміти де вихід з будинку" - they
 * did not, because nothing ever told them: a villager indoors wandered on a
 * random heading and jumped at whichever wall it met.
 */
export function doorCell(p: Part): { x: number; z: number } {
  const x1 = p.x + p.w - 1, z1 = p.z + p.d - 1;
  const midX = (p.x + x1) >> 1;
  const midZ = (p.z + z1) >> 1;
  if (p.facing === 0) return { x: midX, z: p.z };
  if (p.facing === 2) return { x: midX, z: z1 };
  if (p.facing === 1) return { x: p.x, z: midZ };
  return { x: x1, z: midZ };
}

/**
 * The ground a dwelling stands on, walls and doorway included.
 *
 * What "indoors" means to a villager, and it takes in the walls on purpose:
 * somebody standing in their own doorway is still in the house, and the way
 * on from there is out through it rather than back at whatever they wanted.
 * The two cells either side of the doorway - `porchCell` and `matCell` - are
 * what they actually walk to.
 */
export function roomBox(p: Part): {
  x0: number; x1: number; z0: number; z1: number;
} {
  return { x0: p.x, x1: p.x + p.w - 1, z0: p.z, z1: p.z + p.d - 1 };
}

/**
 * The two cells either side of the doorway: one out, one in.
 *
 * A door is a thing you walk *through*, and neither aiming at it nor aiming
 * past it works. Aiming at it leaves you standing in it. Aiming at what you
 * want aims you at a wall. So there is a point one step outside and a point
 * one step inside, and leaving or entering means walking to the far one -
 * at which point you are plainly out or plainly in, and the real errand
 * takes over.
 *
 * Both were needed before this stopped chattering: with only the outside
 * one, a man coming home stopped in the doorway, counted as outdoors, and
 * turned round for the doorway again.
 */
export function porchCell(p: Part): { x: number; z: number } {
  return stepFromDoor(p, 1);
}

export function matCell(p: Part): { x: number; z: number } {
  return stepFromDoor(p, -1);
}

function stepFromDoor(p: Part, away: number): { x: number; z: number } {
  const door = doorCell(p);
  // `Part.facing` is which wall the door is in: 0 north, 1 west, 2 south,
  // 3 east - so out is away from the middle of the house.
  if (p.facing === 0) return { x: door.x, z: door.z - away };
  if (p.facing === 2) return { x: door.x, z: door.z + away };
  if (p.facing === 1) return { x: door.x - away, z: door.z };
  return { x: door.x + away, z: door.z };
}

/** Where the скриня stands: the corner opposite the піч. */
export function chestCell(p: Part): { x: number; z: number } {
  return { x: p.x + p.w - 2, z: p.z + 1 };
}

/** A барн: open framing, a wide door, and the same straw over it. */
function barn(put: Put, p: Part, floor: number): void {
  cottage(put, { ...p, kind: "господарська" }, floor, 4, BlockId.PineLog);
  const x1 = p.x + p.w - 1;
  const z1 = p.z + p.d - 1;
  // A cart-wide door instead of a doorway: this is where the hay goes in.
  const midX = (p.x + x1) >> 1;
  const midZ = (p.z + z1) >> 1;
  if (p.facing === 0 || p.facing === 2) {
    const z = p.facing === 0 ? p.z : z1;
    fill(put, midX - 1, midX + 1, floor + 1, floor + 3, z, z, BlockId.Air);
  } else {
    const x = p.facing === 1 ? p.x : x1;
    fill(put, x, x, floor + 1, floor + 3, midZ - 1, midZ + 1, BlockId.Air);
  }
}

/**
 * Криниця: a well with a roof over it, which is what stands in the square.
 *
 * Water at the bottom rather than a hole: this is the middle of the village
 * and the thing everybody walks to, and a dry shaft reads as unfinished.
 */
function well(put: Put, p: Part, floor: number): void {
  const x1 = p.x + p.w - 1, z1 = p.z + p.d - 1;
  fill(put, p.x, x1, floor, floor + 1, p.z, z1, BlockId.Cobble);
  fill(put, p.x + 1, p.x + 1, floor - 4, floor + 1, p.z + 1, p.z + 1,
    BlockId.Water);
  // Two posts and a little straw roof on top.
  for (const [cx, cz] of [[p.x, p.z], [x1, z1]]) {
    fill(put, cx!, cx!, floor + 2, floor + 4, cz!, cz!, BlockId.OakLog);
  }
  fill(put, p.x - 1, x1 + 1, floor + 5, floor + 5, p.z - 1, z1 + 1,
    BlockId.Thatch);
}

/** Млин: a tower with sails, and the tallest thing for a mile. */
function mill(put: Put, p: Part, floor: number): void {
  const x1 = p.x + p.w - 1, z1 = p.z + p.d - 1;
  fill(put, p.x, x1, floor, floor, p.z, z1, BlockId.Cobble);
  fill(put, p.x, x1, floor + 1, floor + 9, p.z, z1, BlockId.OakPlanks);
  fill(put, p.x + 1, x1 - 1, floor + 1, floor + 9, p.z + 1, z1 - 1,
    BlockId.Air);
  const midX = (p.x + x1) >> 1;
  fill(put, midX, midX, floor + 1, floor + 2, p.z, p.z, BlockId.Air);
  // The cap, and the sails standing out from it.
  fill(put, p.x - 1, x1 + 1, floor + 10, floor + 11, p.z - 1, z1 + 1,
    BlockId.Thatch);
  const sailY = floor + 7;
  for (let i = 1; i <= 5; i++) {
    put(p.x - i, sailY + i, (p.z + z1) >> 1, BlockId.OakPlanks);
    put(x1 + i, sailY - i, (p.z + z1) >> 1, BlockId.OakPlanks);
  }
}

/** Каплиця: one room, a door, and a little tower over it. */
function chapel(put: Put, p: Part, floor: number): void {
  cottage(put, { ...p, kind: "каплиця" }, floor, 5, BlockId.OakLog);
  const midX = (p.x + p.w - 1 + p.x) >> 1;
  const midZ = (p.z + p.d - 1 + p.z) >> 1;
  const top = floor + 6 + (Math.min(p.w, p.d) >> 1);
  fill(put, midX - 1, midX + 1, top, top + 3, midZ - 1, midZ + 1,
    BlockId.OakPlanks);
  fill(put, midX, midX, top + 4, top + 5, midZ, midZ, BlockId.OakLog);
}

/**
 * Город: turned earth in rows, a water channel down the middle, and something
 * growing in it.
 *
 * "Створи їм городи з водою та сажанцями. Додай буряк і нехай в них росте
 * теж." It used to be a fenced box of dry soil - nothing planted, nothing
 * watered, and dry соil is soil nothing *can* grow in, so a village of twelve
 * gardens grew twelve crops of nothing.
 *
 * The channel is one row of water across the middle, which is inside
 * `World.WATER_REACH` of every cell in a plot this size - so the whole bed is
 * `FarmlandWet` and written as such rather than left for the hydration pass,
 * which only ever runs where somebody has dug.
 *
 * Wheat on one side of the water and буряк on the other, at whatever stage
 * the ground says: a garden where everything ripens on the same day is a
 * printed pattern, and a few bare cells are what a bed somebody is actually
 * working looks like.
 */
function garden(put: Put, p: Part, floor: number): void {
  const x1 = p.x + p.w - 1, z1 = p.z + p.d - 1;
  const midZ = (p.z + z1) >> 1;
  const WHEAT = [
    BlockId.WheatCrop0, BlockId.WheatCrop1, BlockId.WheatCrop2,
  ] as const;
  const BEET = [beetCropId(0), beetCropId(1), beetCropId(2)] as const;
  for (let z = p.z; z <= z1; z++) {
    for (let x = p.x; x <= x1; x++) {
      const edge = x === p.x || x === x1 || z === p.z || z === z1;
      if (edge) {
        put(x, floor, z, BlockId.Dirt);
        put(x, floor + 1, z, FENCE);
        continue;
      }
      if (z === midZ) {
        // The channel. A source with earth on both sides of it and the fence
        // beyond that, so there is nowhere for it to run.
        put(x, floor, z, BlockId.Water);
        put(x, floor + 1, z, BlockId.Air);
        continue;
      }
      put(x, floor, z, BlockId.FarmlandWet);
      /*
       * What is in this cell, from the cell itself.
       *
       * The village's own hash on the world position, so the answer is the
       * same whichever column of the world happens to grow this corner of
       * the garden - a plot written twice with two different answers is how
       * a crop ends up half wheat.
       */
      const roll = hash(x, z, 909);
      const stage = roll < 0.3 ? 0 : roll < 0.62 ? 1 : 2;
      const crop = z < midZ ? WHEAT[stage]! : BEET[stage]!;
      put(x, floor + 1, z, roll > 0.94 ? BlockId.Air : crop);
    }
  }
  // A way in, or it is a box of soil.
  put(p.x + (p.w >> 1), floor + 1, p.z, BlockId.Air);
}

/**
 * The lanes: the square, a ring behind the houses, and spokes out to the тин.
 *
 * Earth, not stone. What a village had was the ground worn bare by feet and
 * carts, so it is `Dirt` laid one course into the grass - and laid *under*
 * the buildings' level, so a lane that runs past a хата meets its footing
 * rather than climbing over it.
 */
function lanes(
  put: Put, p: Part, groundAt: (x: number, z: number) => number,
): void {
  const cx = p.x + (p.w >> 1), cz = p.z + (p.d >> 1);
  const ring = (radius: number, wide: number): void => {
    const steps = Math.ceil(radius * 7);
    for (let i = 0; i < steps; i++) {
      const turn = (i / steps) * Math.PI * 2;
      const rx = cx + Math.round(Math.cos(turn) * radius);
      const rz = cz + Math.round(Math.sin(turn) * radius);
      for (let dz = -wide; dz <= wide; dz++) {
        for (let dx = -wide; dx <= wide; dx++) {
          put(rx + dx, groundAt(rx + dx, rz + dz), rz + dz, BlockId.Dirt);
        }
      }
    }
  };
  // The square itself, then the lane behind the inner ring of houses.
  for (let dz = -6; dz <= 6; dz++) {
    for (let dx = -6; dx <= 6; dx++) {
      if (Math.abs(dx) + Math.abs(dz) > 9) continue;
      put(cx + dx, groundAt(cx + dx, cz + dz), cz + dz, BlockId.Dirt);
    }
  }
  ring(27, 1);
  // And out to each of the four ways through the тин.
  for (const [dx, dz] of [[1, 0], [-1, 0], [0, 1], [0, -1]]) {
    for (let d = 6; d <= 59; d++) {
      for (let w = -1; w <= 1; w++) {
        const lx = cx + dx! * d + (dz! !== 0 ? w : 0);
        const lz = cz + dz! * d + (dx! !== 0 ? w : 0);
        put(lx, groundAt(lx, lz), lz, BlockId.Dirt);
      }
    }
  }
}

/** The тин round the village, with four ways through it. */
/**
 * The тин, and the four хвіртки in it.
 *
 * It used to be a circle drawn with `Math.round(cos)` and `Math.round(sin)`,
 * and that is why it looked wrong. A wall block is a post with an arm toward
 * each *orthogonal* neighbour - that is what makes a row of them read as a
 * fence rather than as a row of pillars - and a rasterised circle steps
 * diagonally, so most of its cells had no orthogonal neighbour at all. What
 * went up was a scatter of lone posts with the odd two-cell stub between
 * them, each stub pointing whichever way that part of the circle happened to
 * step: "тин стоїть боком".
 *
 * So it is an octagon of straight runs now - four sides and four corners,
 * every cell touching the next one along an axis, which is the shape a
 * village fence is anyway. The corners are staircases and a staircase is
 * four-connected here: both cells of each step are written, not just the
 * corner point.
 *
 * And a хвіртка in the middle of each side, which is exactly where the four
 * lanes leave the village. There was no way through before at all - the old
 * ring simply left a five-cell hole where a lane crossed it, which is a gap
 * in a fence rather than a gate.
 */
function palisade(
  put: Put, p: Part, groundAt: (x: number, z: number) => number,
): void {
  const cx = p.x + (p.w >> 1), cz = p.z + (p.d >> 1);
  const r = p.w >> 1;
  // How far along each side the corner cut starts. Three eighths reads as a
  // village fence that has gone round something rather than as a box.
  const flat = r - Math.round(r * 0.38);

  /*
   * The ring, as an ordered walk, before anything is written.
   *
   * Ordered because a fence on a hillside has to be told what its
   * neighbours are standing on: two posts a course apart do not touch, and a
   * wall block only grows an arm toward a neighbour at its own height. Each
   * post is carried down to meet whichever of its neighbours is lower, which
   * is what a real fence does on a slope.
   */
  const ring: { x: number; z: number; gate: DoorFacing | null }[] = [];
  const line = (
    fromX: number, fromZ: number, toX: number, toZ: number,
  ): void => {
    let x = fromX, z = fromZ;
    ring.push({ x, z, gate: null });
    while (x !== toX || z !== toZ) {
      // One axis at a time: a diagonal step leaves both cells on their own,
      // which is exactly what the circle this replaced did everywhere.
      if (x !== toX) { x += Math.sign(toX - x); ring.push({ x, z, gate: null }); }
      if (z !== toZ) { z += Math.sign(toZ - z); ring.push({ x, z, gate: null }); }
    }
    ring.pop();   // the far end is the next run's first cell
  };
  line(cx - flat, cz - r, cx + flat, cz - r);
  line(cx + flat, cz - r, cx + r, cz - flat);
  line(cx + r, cz - flat, cx + r, cz + flat);
  line(cx + r, cz + flat, cx + flat, cz + r);
  line(cx + flat, cz + r, cx - flat, cz + r);
  line(cx - flat, cz + r, cx - r, cz + flat);
  line(cx - r, cz + flat, cx - r, cz - flat);
  line(cx - r, cz - flat, cx - flat, cz - r);

  /*
   * A хвіртка where each of the four lanes leaves the village.
   *
   * There was no way through before at all: the old ring left a five-cell
   * hole where a lane crossed it, which is a gap in a fence rather than a
   * gate. The facing is outward, so the panel lies across the lane.
   */
  for (const cell of ring) {
    if (cell.x === cx && cell.z === cz - r) cell.gate = 0;
    else if (cell.x === cx && cell.z === cz + r) cell.gate = 2;
    else if (cell.z === cz && cell.x === cx - r) cell.gate = 3;
    else if (cell.z === cz && cell.x === cx + r) cell.gate = 1;
  }

  const on = ring.map((c) => groundAt(c.x, c.z));
  ring.forEach((c, i) => {
    const before = on[(i - 1 + ring.length) % ring.length]!;
    const after = on[(i + 1) % ring.length]!;
    const here = on[i]!;
    const foot = Math.min(here, before, after) + 1;
    for (let y = foot; y <= here + 1; y++) {
      /*
       * One course, and that is now enough.
       *
       * It was two, because a блок used to be solid or not: a тин could
       * never be taller than the cell it sat in, one course was a thing you
       * stepped over, and the only way to keep orcs out was to stack two -
       * which made the хвіртка a gateway you walked into, since a person is
       * two blocks tall.
       *
       * A тин is a block and a half now, which is what a fence is. One
       * course is higher than a jump, the way through it is one gate with
       * open air over it, and the whole knot comes undone.
       */
      put(c.x, y, c.z, c.gate !== null && y >= here + 1
        ? gateId(c.gate, false) : FENCE);
    }
  });
}

/** Everything a part is made of, written through `put`. */
export function buildPart(
  put: Put, part: Part, floor: number,
  groundAt: (x: number, z: number) => number,
): void {
  switch (part.kind) {
    case "хата": cottage(put, part, floor, 4, BlockId.OakLog); return;
    case "велика хата": cottage(put, part, floor, 5, BlockId.OakLog); return;
    case "козацький": cottage(put, part, floor, 5, BlockId.PineLog); return;
    case "господарська": barn(put, part, floor); return;
    case "криниця": well(put, part, floor); return;
    case "млин": mill(put, part, floor); return;
    case "каплиця": chapel(put, part, floor); return;
    case "город": garden(put, part, floor); return;
    case "дороги": lanes(put, part, groundAt); return;
    case "тин": palisade(put, part, groundAt); return;
  }
}

/**
 * Does this part need the ground cut flat under it?
 *
 * Only the things with a floor. The lanes and the тин follow the country -
 * levelling *their* boxes was what turned a village into a brown square a
 * hundred blocks across with houses dotted on it, because the тин's box is
 * the whole village and the level pass obediently flattened all of it.
 */
export function needsLevelling(kind: Kind): boolean {
  return kind !== "дороги" && kind !== "тин";
}

/** The box a part needs, so a column can tell whether it cares about it. */
export function partBox(part: Part): {
  x0: number; x1: number; z0: number; z1: number;
} {
  // Generous: roofs oversail, sails stand out, and a chimney is taller than
  // anything. Being wrong small here means a building with a slice missing.
  // The rings are already their own size; everything else oversails a
  // little - roofs by a block, sails by five, a chimney by nothing sideways.
  const pad = part.kind === "млин" ? 7
    : part.kind === "дороги" || part.kind === "тин" ? 2 : 3;
  return {
    x0: part.x - pad, x1: part.x + part.w - 1 + pad,
    z0: part.z - pad, z1: part.z + part.d - 1 + pad,
  };
}
